import { Info, X } from 'lucide-react'
import { toast } from 'sonner'

import { useState } from 'react'

import { ConfirmDialog } from '@/components/atoms'
import { Button } from '@/components/ui/button'
import { describeCounts, useRemoveSampleData, useSampleData } from '@/hooks/features/sample-data/useSampleData'
import { useBusiness } from '@/contexts/BusinessContext'

const dismissKey = (businessId: string) => `sample-data-banner-dismissed:${businessId}`

const readDismissed = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

/** Shown while the store still has the demo's sample products: edit them, or remove them all at once. */
export function SampleDataBanner() {
  const { profile } = useBusiness()
  const businessId = (profile as { _id?: string } | null)?._id ?? 'current'
  const [dismissed, setDismissed] = useState(() => readDismissed(dismissKey(businessId)))
  const [confirming, setConfirming] = useState(false)
  const { data } = useSampleData()
  const remove = useRemoveSampleData()

  if (dismissed || !data?.has_sample) return null

  const dismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(dismissKey(businessId), '1')
    } catch {
      // Storage blocked (private mode): the banner stays hidden for this visit only.
    }
  }

  const confirmRemove = () => {
    remove.mutate(undefined, {
      onSuccess: (counts) => {
        const summary = describeCounts(counts)
        toast.success(summary ? `Removed ${summary}` : 'Sample data removed')
        setConfirming(false)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-info-lighter px-4 py-3 sm:flex-row sm:items-center" role="region" aria-label="Sample data">
      <Info aria-hidden="true" className="size-4 shrink-0 text-info" />
      <p className="flex-1 text-sm text-info-dark">
        This store has sample products from the demo. Edit them or remove them all.
      </p>
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setConfirming(true)}>
          Remove sample data
        </Button>
        <Button variant="ghost" size="icon" aria-label="Dismiss" onClick={dismiss} className="text-info-dark hover:bg-info/16">
          <X />
        </Button>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Remove all sample data?"
        description={
          data.counts && describeCounts(data.counts)
            ? `This deletes ${describeCounts(data.counts)} copied from the demo. Your own products stay. This cannot be undone.`
            : 'This deletes every sample record copied from the demo. Your own products stay. This cannot be undone.'
        }
        confirmText="Remove sample data"
        variant="destructive"
        loading={remove.isPending}
        onConfirm={confirmRemove}
      />
    </div>
  )
}
